import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { X, MessageCircle, Maximize2, ChevronUp, ChevronDown } from 'lucide-react';
import useStore from '@/store/store';
import { useIsMobile } from '@/utils/useIsMobile';
import { cn } from '@/utils/merge';
import { commApi } from './commApi';
import { useCommStore } from './commStore';
import { Inbox } from './Inbox';
import { NotificationToggle } from './CommunicationCenter';

const COLUMN_WIDTH = 360;
// On a phone: the bottom half of the screen.
const MOBILE_HEIGHT = '50dvh';
// Pop-ups at once for new messages; beyond that, one that counts the rest.
const MAX_POPUPS = 3;

// Small pop-up for a new message while the column is closed: who wrote what,
// and a button that opens the column on that conversation.
const NewMessageToast = ({ t, heading, title, sender, body, onOpen }) => (
	<div
		className={cn(
			'flex w-[340px] max-w-[calc(100vw-32px)] items-start gap-3 rounded-lg border border-slate-200 bg-white p-3 shadow-lg',
			t.visible ? 'opacity-100' : 'opacity-0',
		)}
	>
		<MessageCircle size={20} className='mt-0.5 shrink-0 text-[#0f9b9d]' aria-hidden />
		<div className='min-w-0 flex-1'>
			<div className='text-[14px] font-semibold text-slate-800'>
				{heading || `Νέο μήνυμα${sender ? ` από ${sender}` : ''}`}
			</div>
			{title && title !== sender && (
				<div className='truncate text-[12px] text-slate-500'>{title}</div>
			)}
			{body && <div className='mt-1 line-clamp-2 text-[13px] text-slate-700'>{body}</div>}
			<button
				type='button'
				onClick={() => {
					toast.dismiss(t.id);
					onOpen();
				}}
				className='mt-2 rounded-md bg-[#0f9b9d] px-3 py-1 text-[13px] font-medium text-white hover:bg-[#0c8486]'
			>
				Άνοιγμα
			</button>
		</div>
		<button
			type='button'
			onClick={() => toast.dismiss(t.id)}
			className='rounded-md p-0.5 text-slate-400 hover:bg-slate-100'
			aria-label='Κλείσιμο ειδοποίησης'
		>
			<X size={16} />
		</button>
	</div>
);

// The messages as a column on the left of every page, so it can stay open
// next to Live. It opens and closes from Μηνύματα in the menu. When a new
// message arrives, shops get the column opened by itself; admins get a
// pop-up instead and open the column when they want. On a phone it takes the
// bottom half of the screen, so the page (Live) stays usable above it, and
// can be enlarged to the whole screen. Admins don't get it on the Μηνύματα
// page itself.
export const ShopMessagesPanel = () => {
	const { user } = useStore();
	const location = useLocation();
	const me = useCommStore(s => s.me);
	const open = useCommStore(s => s.panelOpen);
	const conversationId = useCommStore(s => s.panelConversationId);
	const openPanel = useCommStore(s => s.openPanel);
	const closePanel = useCommStore(s => s.closePanel);
	const setConversation = useCommStore(s => s.setPanelConversation);
	const setColumnWidth = useCommStore(s => s.setColumnWidth);
	const isMobile = useIsMobile();
	const version = useCommStore(s => s.version);
	const lastUnread = useRef(null);
	// Admins: unread count per conversation at the last look, to tell which
	// conversations got something new (null until the first look).
	const seenUnread = useRef(null);
	const asideRef = useRef(null);
	// Phone only: the bottom half (false) or the whole screen (true).
	const [expanded, setExpanded] = useState(false);

	const isShop = user?.role === 'shop';
	const isMaster = user?.role === 'master';
	// The Super Admin gets only the city chosen in his header here (column,
	// pop-ups); other cities only when he looks for them on the Μηνύματα page.
	const headerCityId = isMaster ? user?.cityLocation?.id : undefined;
	const onMessagesPage = location.pathname === '/messages';
	const visible = open && !(onMessagesPage && !isShop);
	const shown = !!me?.canUse && visible;

	// Tell the pages how much width the column takes from them (none on a
	// phone, where it covers the screen instead).
	useEffect(() => {
		setColumnWidth(shown && !isMobile ? COLUMN_WIDTH : 0);
		return () => setColumnWidth(0);
	}, [shown, isMobile, setColumnWidth]);

	// New unread message: shops get the column opened.
	useEffect(() => {
		if (!isShop) return;
		const unread = me?.unread ?? null;
		const previous = lastUnread.current;
		lastUnread.current = unread;
		if (previous !== null && unread !== null && unread > previous) openPanel();
	}, [me?.unread, isShop, openPanel]);

	// Admins: a pop-up for every conversation that got new messages since the
	// last look (several can arrive together), unless they are already
	// looking at their messages. Checked on every update and unread change.
	useEffect(() => {
		if (isShop || !me?.canUse) return undefined;
		let alive = true;
		commApi.conversations({ unread: 1, limit: 50, cityId: headerCityId }).then(res => {
			if (!alive || res?.error) return;
			const before = seenUnread.current;
			seenUnread.current = new Map(res.conversations.map(c => [c.id, c.unread]));
			if (!before) return; // first look: only remember
			const { panelOpen } = useCommStore.getState();
			if (panelOpen || window.location.pathname === '/messages') return;
			const fresh = res.conversations.filter(c => c.unread > (before.get(c.id) || 0));
			fresh
				.slice(0, MAX_POPUPS)
				.forEach(c =>
					toast.custom(
						t => (
							<NewMessageToast
								t={t}
								title={c.title}
								sender={c.lastMessage?.senderName}
								body={c.lastMessage?.body}
								onOpen={() => openPanel(c.id)}
							/>
						),
						{ id: `comm-${c.id}`, duration: 10000, position: 'top-right' },
					),
				);
			if (fresh.length > MAX_POPUPS) {
				const more = fresh.length - MAX_POPUPS;
				toast.custom(
					t => (
						<NewMessageToast
							t={t}
							heading='Νέα μηνύματα'
							title={`Και σε ${more === 1 ? 'άλλη 1 συνομιλία' : `άλλες ${more} συνομιλίες`}`}
							onOpen={() => openPanel()}
						/>
					),
					{ id: 'comm-more', duration: 10000, position: 'top-right' },
				);
			}
		});
		return () => {
			alive = false;
		};
	}, [me?.unread, me?.canUse, version, isShop, openPanel, headerCityId]);

	// From under the menu (whatever its height) to the bottom of the screen,
	// also while the page scrolls.
	useEffect(() => {
		if (!shown || isMobile) return undefined;
		const fit = () => {
			const main = document.querySelector('.app-main-container');
			const top = Math.max(0, main ? main.getBoundingClientRect().top : 0);
			if (asideRef.current) asideRef.current.style.height = `${window.innerHeight - top}px`;
		};
		fit();
		window.addEventListener('scroll', fit, { passive: true });
		window.addEventListener('resize', fit);
		return () => {
			window.removeEventListener('scroll', fit);
			window.removeEventListener('resize', fit);
		};
	}, [shown, isMobile]);

	// Phone, bottom half: room under the page so its end can still be
	// scrolled up above the messages.
	useEffect(() => {
		if (!shown || !isMobile || expanded) return undefined;
		const main = document.querySelector('.app-main-container');
		if (!main) return undefined;
		main.style.paddingBottom = MOBILE_HEIGHT;
		return () => {
			main.style.paddingBottom = '';
		};
	}, [shown, isMobile, expanded]);

	if (!shown) return null;

	return (
		<aside
			ref={asideRef}
			className={cn(
				'flex flex-col bg-white',
				!isMobile &&
					'sticky top-0 z-20 h-[calc(100dvh-80px)] shrink-0 self-start border-r border-slate-200',
				isMobile && expanded && 'fixed inset-0 z-50',
				isMobile &&
					!expanded &&
					'fixed inset-x-0 bottom-0 z-50 rounded-t-2xl border-t border-slate-200 shadow-[0_-8px_24px_rgba(0,0,0,0.18)]',
			)}
			style={
				isMobile ? (expanded ? undefined : { height: MOBILE_HEIGHT }) : { width: COLUMN_WIDTH }
			}
			aria-label='Μηνύματα'
		>
			<div className='flex items-center gap-2 border-b border-slate-200 px-3 py-2'>
				<MessageCircle size={18} className='text-[#0f9b9d]' aria-hidden />
				<span className='text-[15px] font-semibold text-slate-800'>Μηνύματα</span>
				<div className='ml-auto flex items-center gap-2'>
					<NotificationToggle />
					{!isShop && (
						<Link
							to={`/messages${conversationId ? `?c=${conversationId}` : ''}`}
							className='rounded-md p-1 text-slate-500 hover:bg-slate-100'
							aria-label='Όλα τα μηνύματα'
							title='Όλα τα μηνύματα'
						>
							<Maximize2 size={18} />
						</Link>
					)}
					{isMobile && (
						<button
							type='button'
							onClick={() => setExpanded(v => !v)}
							className='rounded-md p-1 text-slate-500 hover:bg-slate-100'
							aria-label={expanded ? 'Μίκρυνση μηνυμάτων' : 'Μεγάλωμα μηνυμάτων'}
							title={expanded ? 'Μίκρυνση' : 'Μεγάλωμα'}
						>
							{expanded ? <ChevronDown size={20} /> : <ChevronUp size={20} />}
						</button>
					)}
					<button
						type='button'
						onClick={closePanel}
						className='rounded-md p-1 text-slate-500 hover:bg-slate-100'
						aria-label='Κλείσιμο μηνυμάτων'
						title='Κλείσιμο'
					>
						<X size={20} />
					</button>
				</div>
			</div>
			<div className='flex min-h-0 flex-1'>
				<Inbox
					key={conversationId || 'panel'}
					compact
					initialId={conversationId}
					onSelect={setConversation}
					cityId={headerCityId}
					extraFilters={headerCityId ? { cityId: headerCityId } : undefined}
				/>
			</div>
		</aside>
	);
};
